import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime, formatNumber } from "@/lib/format";
import { formatUsd } from "@/components/LlmExtractionFields";

type LlmExtraction = components["schemas"]["BillLlmExtractionResponse"];

interface LlmExtractionsTableProps {
  organisationId?: string;
  companyId?: string;
  refreshKey?: number;
}

// Company is the one conditional column, so the empty-state colSpan is derived, not hard-coded.
const BASE_COLUMN_COUNT = 8;

export function LlmExtractionsTable({
  organisationId,
  companyId,
  refreshKey,
}: LlmExtractionsTableProps) {
  const showCompany = !companyId;
  const columnCount = BASE_COLUMN_COUNT + (showCompany ? 1 : 0);

  const pagination = useCursorPagination({ initialLimit: 50 });
  const { reset } = pagination;

  const [rows, setRows] = useState<LlmExtraction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    reset();
  }, [organisationId, companyId, refreshKey, reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchRows() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/llm-extractions", {
        params: {
          query: {
            Cursor: pagination.cursor,
            Limit: pagination.limit,
            OrganisationId: organisationId,
            // Filtered as well as hidden, or other companies' extractions would show with nothing to tell them apart.
            CompanyId: companyId,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load bill extractions.");
      } else {
        setRows(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchRows();
    return () => {
      cancelled = true;
    };
  }, [organisationId, companyId, refreshKey, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Bill</TableHead>
              <TableHead>Organisation</TableHead>
              {showCompany && <TableHead>Company</TableHead>}
              <TableHead>Model</TableHead>
              <TableHead className="text-right">Input</TableHead>
              <TableHead className="text-right">Output</TableHead>
              <TableHead className="text-right">Total cost</TableHead>
              <TableHead className="text-right">Duration</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="text-muted-foreground h-24 text-center">
                  No bill extractions.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.billId}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatDateTime(row.extractedAt)}
                  </TableCell>
                  <TableCell>
                    {/* No organisation on a company row: it was a company inbox bill when extracted. That page
                        forwards to /bills/:id once routed. */}
                    <IdLink
                      to={
                        !row.organisationId && row.companyId
                          ? `/company-inbox-bills/${row.billId}`
                          : `/bills/${row.billId}`
                      }
                      id={row.billId}
                    />
                  </TableCell>
                  <TableCell>
                    <IdLink to={`/organisations/${row.organisationId}`} id={row.organisationId} />
                  </TableCell>
                  {/* Only set for company-inbox documents; it survives routing, so a company bill shows both owners. */}
                  {showCompany && (
                    <TableCell>
                      <IdLink to={`/companies/${row.companyId}`} id={row.companyId} />
                    </TableCell>
                  )}
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {row.model}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(row.inputTokens)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(row.outputTokens)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatUsd(row.totalCostUsd)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(Math.round(Number(row.durationMs ?? 0)))} ms
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      )}

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        limitOptions={[20, 50, 100]}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}

function IdLink({ to, id }: { to: string; id?: string | null }) {
  if (!id) return <span className="text-muted-foreground">-</span>;
  return (
    <Link to={to}>
      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
        {id.slice(0, 8)}...
      </Badge>
    </Link>
  );
}
